import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasFeatureAccess } from '@/config/featureAccess';
import NavigationBar from '@/components/ui/NavigationBar';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import { generateUGCPlan } from '@/services/ugcService';
import { NovaChatProvider } from '@/contexts/NovaChatContext';
import NovaChat from '@/components/NovaChat';
import UGCLayout from '@/components/layouts/UGCLayout';

const defaultForm = {
  brand: '',
  product: '',
  platform: 'tiktok',
  goal: 'conversion',
  tone: 'hype',
  cta: 'Shop now',
  audience: '',
  hooks: 3
};

const UGCWorkspace = () => {
  const { profile } = useAuth();
  const { ugcTier } = useSubscription();
  const [form, setForm] = useState(defaultForm);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [outreach, setOutreach] = useState(null);
  const [invoice, setInvoice] = useState(null);

  const userTier = ugcTier || profile?.ugc_tier || 'free';
  const canAccess = hasFeatureAccess('app/ugc-scripts', userTier);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const aiResult = await generateUGCPlan(form);

      const platformLabel = form.platform === 'tiktok'
        ? 'TikTok'
        : form.platform === 'instagram'
          ? 'Instagram Reels'
          : 'YouTube Shorts';

      setResult({
        hooks: aiResult.hooks || [],
        script: aiResult.script || [],
        shotList: aiResult.shotList || [],
        platformLabel,
        platformNotes: aiResult.platformNotes || []
      });
    } catch (err) {
      console.error('UGC AI generation failed, falling back:', err);
      const brand = form.brand || 'Your brand';
      const product = form.product || 'your product';
      const audience = form.audience || 'your target audience';
      const platformLabel = form.platform === 'tiktok' ? 'TikTok' : form.platform === 'instagram' ? 'Instagram Reels' : 'YouTube Shorts';

      const hookTemplates = [
        `POV: You need ${product} before your next scroll ends`,
        `I tried ${product} so you don't have to — here’s the real tea`,
        `Stop wasting money on ${product} dupes — this is the one`,
        `${brand} sent me this — honest review in 30 seconds`,
        `Creators: here’s how I use ${product} to boost ${audience}`,
        `3 reasons ${product} is my new ${platformLabel} cheat code`
      ];

      const tonePrefix = {
        hype: '🔥',
        casual: '👀',
        educational: '💡',
        authority: '✅'
      }[form.tone] || '';

      const hooks = hookTemplates
        .slice(0, Math.max(1, form.hooks || 3))
        .map((h) => `${tonePrefix} ${h}`);

      const script = [
        { label: 'Hook', copy: hooks[0] || hookTemplates[0] },
        { label: 'Problem', copy: `Ever feel like ${audience} scrolls right past? ${brand} fixes that in 10 seconds.` },
        { label: 'Social Proof', copy: `I used ${product} for a week — engagement up +27% on ${platformLabel}.` },
        { label: 'Demo', copy: `Show how ${product} works with quick cuts and captions.` },
        { label: 'CTA', copy: `${form.cta || 'Grab yours now'} — link in bio/description.` }
      ];

      const shotList = [
        {
          shot: 'Opening selfie',
          framing: 'Front camera, 0.5x for energy',
          action: 'Deliver hook with on-screen text + caption',
          text: hooks[0]
        },
        {
          shot: 'Problem B-roll',
          framing: 'Over-the-shoulder',
          action: `Show the pain point (${audience} struggle) with quick cuts`,
          text: 'The struggle is real'
        },
        {
          shot: 'Demo / How-to',
          framing: 'Close-up product usage',
          action: `Highlight ${product} in action; add pop-up callouts`,
          text: 'Step 1 / Step 2'
        },
        {
          shot: 'Social proof',
          framing: 'Screen recording overlay',
          action: 'Show analytics/DM/social proof snippets',
          text: '+27% engagement'
        },
        {
          shot: 'CTA',
          framing: 'Front camera',
          action: `Restate benefit and CTA for ${platformLabel}`,
          text: form.cta || 'Shop now'
        }
      ];

      const platformNotes = {
        tiktok: ['Keep 9–15s; front-load hook; add trending audio quietly', 'Use captions + on-screen text; fast cuts'],
        instagram: ['Aim 15–30s; reuse as Reel + Story', 'Cover bottom-safe areas for IG UI'],
        youtube: ['Keep 30–45s; clearer VO mix', 'Add end card with CTA text']
      }[form.platform] || [];

      setResult({
        hooks,
        script,
        shotList,
        platformLabel,
        platformNotes
      });
    } finally {
      setLoading(false);
    }
  };

  const buildOutreach = () => {
    const brand = form.brand || 'Brand';
    const product = form.product || 'product';
    const audience = form.audience || 'your audience';
    const packageLine = 'Package ideas: 3 videos + raw cuts + 30-day paid usage';

    const email = `
Subject: Quick UGC concept for ${brand}

Hey {{name}},
I shot a fast concept for ${product} aimed at ${audience}. I can deliver 3x TikTok/Reels-ready videos with raw cuts and paid usage included.

Ideas:
- Hook 1: "${result?.hooks?.[0] || 'POV: you need this before your next scroll ends'}"
- Shot list: ${result?.shotList?.[0]?.shot || 'Opening selfie'} + demo + social proof + CTA
- CTA: ${form.cta || 'Shop now'}

${packageLine}
Want me to shoot a sample this week?`;

    const dm = `Hey! I’m a UGC creator — have a TikTok/Reels concept for ${brand}. ${packageLine}. Want a sample?`;

    const followUp = `Bumping this — can shoot a sample for ${product} this week. Includes 3 edits + raw + 30-day usage. Down to test?`;

    setOutreach({ email, dm, followUp });
  };

  const buildInvoice = () => {
    const base = [
      { name: 'Starter', price: 350, items: ['1x UGC video', '1x hook alt', 'Organic usage 30 days'] },
      { name: 'Standard', price: 750, items: ['3x UGC videos', '3x hooks', 'Raw cuts + captions', 'Organic usage 60 days'] },
      { name: 'Elite', price: 1200, items: ['5x UGC videos', '5x hooks', 'Raw + 3 aspect ratios', 'Paid usage 90 days', '1x whitelisting ad script'] }
    ];
    setInvoice(base);
  };

  const handleCopy = (text) => {
    navigator.clipboard?.writeText(text);
  };

  const renderPreview = () => (
    <div className="mb-10 overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-lg">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-200">
            <Icon name="Crown" size={14} />
            Creator / Elite
          </span>
          <span className="text-xs text-amber-700">UGC Scripts</span>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">UGC script + shot list builder</h3>
        <p className="text-gray-700 mb-4">Generate hooks, beats, and shot lists plus outreach copy and package ideas. Upgrade to Creator to unlock.</p>
        <Button onClick={() => (window.location.href = '/ugc#plans')} className="bg-amber-500 hover:bg-amber-600 text-white">
          View Creator plans
        </Button>
      </div>
    </div>
  );

  const renderResults = () => {
    if (!result) return null;

    return (
      <div className="space-y-6">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-xl backdrop-blur">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <div>
              <p className="text-xs uppercase text-white/60 mb-1">Platform</p>
              <h3 className="text-xl font-semibold text-white">{result.platformLabel}</h3>
              <p className="text-sm text-white/60">Hook stack + script + shots tailored to your pacing.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setResult(null)} className="text-white border-white/20 bg-white/5 hover:bg-white/10">
                Reset
              </Button>
              <Button size="sm" onClick={buildOutreach} className="bg-amber-400 text-slate-950 hover:bg-amber-300">
                Build outreach copy
              </Button>
              <Button size="sm" variant="secondary" onClick={buildInvoice} className="bg-white/10 text-white border-white/20 hover:bg-white/20">
                Build invoice
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-amber-400/15 via-white/5 to-white/0 p-4 shadow-sm">
              <p className="text-xs uppercase text-white/60 mb-1">Hooks</p>
              <ul className="space-y-2 text-white">
                {result.hooks.map((hook, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Icon name="Sparkles" size={16} className="mt-1 text-amber-300" />
                    <span>{hook}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-sm">
              <p className="text-xs uppercase text-white/60 mb-1">Platform notes</p>
              <ul className="space-y-2 text-white/90">
                {result.platformNotes.map((note, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Icon name="Info" size={16} className="mt-1 text-amber-300" />
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase text-white/60">Script</p>
              <Button size="sm" variant="ghost" className="text-white border-white/20 bg-white/5 hover:bg-white/10" onClick={() => handleCopy(result.script.map((b) => `${b.label}: ${b.copy}`).join('\n'))}>
                Copy script
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {result.script.map((block, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-gradient-to-br from-white/5 to-amber-500/10 p-3">
                  <p className="text-xs uppercase text-white/60 mb-1">{block.label}</p>
                  <p className="text-white/90">{block.copy}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase text-white/60">Shot list</p>
              <Button size="sm" variant="ghost" className="text-white border-white/20 bg-white/5 hover:bg-white/10" onClick={() => handleCopy(result.shotList.map((s) => `${s.shot}: ${s.action}`).join('\n'))}>
                Copy shot list
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {result.shotList.map((shot, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-gradient-to-br from-amber-500/10 via-white/5 to-white/0 p-3">
                  <p className="text-sm font-semibold text-white">{shot.shot}</p>
                  <p className="text-sm text-white/70">{shot.framing}</p>
                  <p className="text-sm text-white/90 mt-1">{shot.action}</p>
                  <p className="text-xs text-amber-200 mt-1">{shot.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {outreach && (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-xl backdrop-blur">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white">Outreach copy</h3>
              <Button size="sm" variant="ghost" className="text-white border-white/20 bg-white/5 hover:bg-white/10" onClick={() => handleCopy(`${outreach.email}\n\n${outreach.dm}\n\n${outreach.followUp}`)}>
                Copy all
              </Button>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="rounded-xl border border-white/10 bg-gradient-to-br from-amber-500/15 via-white/5 to-white/0 p-3">
                <p className="text-xs uppercase text-white/60 mb-1">Email</p>
                <p className="text-sm text-white whitespace-pre-wrap">{outreach.email}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs uppercase text-white/60 mb-1">DM</p>
                <p className="text-sm text-white whitespace-pre-wrap">{outreach.dm}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs uppercase text-white/60 mb-1">Follow-up</p>
                <p className="text-sm text-white whitespace-pre-wrap">{outreach.followUp}</p>
              </div>
            </div>
          </div>
        )}

        {invoice && (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-xl backdrop-blur">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white">Invoice builder</h3>
              <Button size="sm" variant="ghost" className="text-white border-white/20 bg-white/5 hover:bg-white/10" onClick={() => setInvoice(null)}>
                Clear
              </Button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {invoice.map((pkg) => (
                <div key={pkg.name} className="rounded-xl border border-white/10 bg-gradient-to-br from-amber-500/15 via-white/5 to-white/0 p-4">
                  <p className="text-sm font-semibold text-white mb-1">{pkg.name}</p>
                  <p className="text-2xl font-bold text-amber-200 mb-2">${pkg.price}</p>
                  <ul className="text-sm text-white/90 space-y-1">
                    {pkg.items.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <Icon name="Check" size={14} className="text-amber-200 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const ugcNavItems = [
    { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
    { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
    { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
    { path: '/app/ugc-community', label: 'Community', icon: 'MessageCircle' },
    { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
    { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
  ];

  return (
    <UGCLayout className="pb-20">
      <NavigationBar items={ugcNavItems} hideMore hideLocks homePath="/app/ugc-dashboard" />
      <main className="max-w-6xl mx-auto px-4 py-10 mb-20">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-amber-500/20 via-slate-900 to-slate-950 px-6 py-8 mb-10 shadow-xl">
          <div className="absolute -top-10 -right-10 h-36 w-36 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-12 -left-8 h-40 w-40 rounded-full bg-amber-500/20 blur-3xl" />
          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-amber-100 text-xs font-semibold border border-white/10 mb-3">
                <Icon name="Video" size={14} />
                UGC Creator Workspace
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Script & Shot List Generator</h1>
              <p className="text-amber-100/80 max-w-2xl">
                Build platform-specific hooks, scripts, and shot lists in minutes. Optimized pacing for TikTok, IG Reels, and Shorts with ready-to-send outreach.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
              {[
                { label: 'Average hook saves', value: '37%', tone: 'from-amber-400/30 to-amber-200/30' },
                { label: 'Delivery speed', value: '3 scripts/min', tone: 'from-white/10 to-amber-300/10' },
                { label: 'Shot list depth', value: '5-7 shots', tone: 'from-amber-500/20 to-white/5' },
                { label: 'CTA library', value: '40+ lines', tone: 'from-white/10 to-amber-500/10' }
              ].map((item) => (
                <div
                  key={item.label}
                  className={`rounded-2xl border border-white/10 bg-gradient-to-br ${item.tone} px-4 py-3 text-white shadow-sm`}
                >
                  <p className="text-[11px] uppercase tracking-wide text-white/70">{item.label}</p>
                  <p className="text-lg font-semibold">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {!canAccess && renderPreview()}

        {canAccess && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 shadow-lg backdrop-blur">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Icon name="Settings" size={18} /> Inputs
                </h3>
                <form className="space-y-4" onSubmit={handleGenerate}>
                  <div>
                    <label className="text-sm font-medium text-white/90 block mb-1">Brand</label>
                    <input
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40"
                      value={form.brand}
                      onChange={(e) => setForm({ ...form, brand: e.target.value })}
                      placeholder="Brand or client name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white/90 block mb-1">Product / Offer</label>
                    <input
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40"
                      value={form.product}
                      onChange={(e) => setForm({ ...form, product: e.target.value })}
                      placeholder="Serum, camera, app, etc."
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-white/90 block mb-1">Platform</label>
                      <select
                        className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                        value={form.platform}
                        onChange={(e) => setForm({ ...form, platform: e.target.value })}
                      >
                        <option value="tiktok">TikTok</option>
                        <option value="instagram">Instagram Reels</option>
                        <option value="youtube">YouTube Shorts</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-white/90 block mb-1">Goal</label>
                      <select
                        className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                        value={form.goal}
                        onChange={(e) => setForm({ ...form, goal: e.target.value })}
                      >
                        <option value="awareness">Awareness</option>
                        <option value="engagement">Engagement</option>
                        <option value="conversion">Conversion</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-white/90 block mb-1">Tone</label>
                      <select
                        className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                        value={form.tone}
                        onChange={(e) => setForm({ ...form, tone: e.target.value })}
                      >
                        <option value="hype">Hype / Energetic</option>
                        <option value="casual">Casual / Friendly</option>
                        <option value="educational">Educational</option>
                        <option value="authority">Authority / Proof-led</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-white/90 block mb-1">CTA</label>
                      <input
                        className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40"
                        value={form.cta}
                        onChange={(e) => setForm({ ...form, cta: e.target.value })}
                        placeholder="Shop now, Apply, DM me, etc."
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white/90 block mb-1">Audience</label>
                    <input
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40"
                      value={form.audience}
                      onChange={(e) => setForm({ ...form, audience: e.target.value })}
                      placeholder="Busy moms, SaaS founders, fitness creators..."
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white/90 block mb-1">Hook variations</label>
                    <input
                      type="number"
                      min={1}
                      max={6}
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                      value={form.hooks}
                      onChange={(e) => setForm({ ...form, hooks: Number(e.target.value) || 3 })}
                    />
                  </div>
                  <Button type="submit" fullWidth disabled={loading} className="bg-amber-400 text-slate-950 hover:bg-amber-300 font-semibold">
                    {loading ? 'Generating...' : 'Generate script + shot list'}
                  </Button>
                </form>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-6">
              {!result && (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-white/70 text-center shadow-lg backdrop-blur">
                  <p className="text-sm">Fill the form and generate to see hooks, script beats, shot lists, outreach, and packages.</p>
                </div>
              )}
              {renderResults()}
            </div>
          </div>
        )}
      </main>
      <NovaChat />
    </UGCLayout>
  );
};

const UGCWorkspaceWithProvider = () => (
  <NovaChatProvider>
    <UGCWorkspace />
  </NovaChatProvider>
);

export default UGCWorkspaceWithProvider;
